import * as HLF from "html-living-framework";
import * as PIXI from "pixi.js";
import {Application} from "../Application";
import {Anchor} from "../core/Anchor";
import {Ease} from "../core/Animatable";
import {Container} from "../core/Container";
import {Callback} from "../core/Reflection";
import {Rect} from "../core/Shape";
import {Times} from "../core/Signal";
import {Sprite} from "../core/Sprite";
import {Assets, StoryAssets, StoryAudioAssets, StoryInjectionAssets} from "../data/Assets";
import {BooksData, IBookData} from "../data/BooksData";
import {IChooseStoryData} from "../data/IChooseStoryData";
import {SceneCreatorStory} from "../data/SceneCreatorStory";
import {BookAnimation} from "../gameobjects/BookAnimation";
import {BookButton} from "../gameobjects/BookButton";
import {Characters} from "../gameobjects/Characters";
import {DragonButton} from "../gameobjects/DragonButton";
import {Focusables} from "../gameobjects/Focusables";
import {MuteButton} from "../gameobjects/MuteButton";
import {ShelfButton} from "../gameobjects/ShelfButton";
import {BookAnimationLayout_main} from "../layout/BookAnimationLayout";
import {ChooseStoryLayout_main} from "../layout/ChooseStoryLayout";
import {AnalyticsManager} from "../managers/AnalyticsManager";
import {AudioChannel} from "../managers/AudioChannel";
import {GameManager} from "../managers/GameManager";
import {PlayMode, VoiceOverManager} from "../managers/VoiceOverManager";
import {AbstractGameState} from "./AbstractGameState";
import {SceneCreator} from "./SceneCreator";
import {StoryCreator} from "./StoryCreator";

export class ChooseStory extends AbstractGameState {

    public readonly main = this.addChild(new Container());
    public readonly background = this.main.addChild(new Background());
    public readonly controls = this.main.addChild(new Container());
    public readonly storyButtons = BooksData.map((bookData) => {
        const b = this.controls.addChild(new BookButton(bookData));
        b.onDown.add(() => {
            this.onClickBook(b, bookData);
        });
        return b;
    });
    public readonly buttonFreePlayGroup = this.controls.addChild(new ButtonFreePlayGroup(() => {
        this.lockScreen();
        this.stopIdleInterval();
        this.characters.hideCharacters(() => {
            const gameManager = GameManager.instance;
            gameManager.story = SceneCreatorStory;
            gameManager.trackAllScenes();
            this.gotoNextState(SceneCreator.NAME);
        });
    }));
    public readonly buttonDragon = this.controls.addChild(new DragonButton());

    public readonly bookLayer = this.main.addChild(new Container());
    public readonly bookOverlay = new Rect(100, 100, 0, 0.6, Anchor.middleCenter);
    public readonly book = new BookAnimation();
    public readonly bookSmall = new (class extends Container {
        public readonly book = {};
    })();
    public readonly bookLargeRight = new BookLarge();
    public readonly bookLargeLeft = new BookLarge();

    public readonly characters = this.main.addChild(new Characters());
    public readonly characterXavier = {x: 0, y: 0};
    public readonly characterYadina = {x: 0, y: 0};
    public readonly characterBrad = {x: 0, y: 0};

    public readonly buttonMute = this.addChild(new MuteButton());

    private _idleInterval?: number;
    private _lastIdleVO?: string;

    public init(pSize: PIXI.Point, pData?: IChooseStoryData) {
        super.init(pSize);

        ChooseStoryLayout_main.call(this);
        BookAnimationLayout_main.call(this);
        this.main.scale.set(0.5);

        this.bookOverlay.interactive = true;
        this.addChildAt(this.book, 0); // preload book animation textures into GPU
        this.book.visible = false;

        Focusables.add(
            ...this.storyButtons.filter((it) => it.enabled),
            this.buttonDragon,
            this.buttonFreePlayGroup.buttonFreePlay,
            this.buttonMute,
        );

        const completeStories = GameManager.instance.completeStories;
        const allStoriesComplete = BooksData.filter((it) => !!it.story).length <= completeStories;

        this.buttonFreePlayGroup.isUnlocked = false; // allStoriesComplete; Hide the free play group for now
        this.buttonFreePlayGroup.visible = false;

        const nextStoryIndex = completeStories + 1;
        for (const button of this.storyButtons) {
            button.initHoverVO();
            if (button.bookData.story &&
                button.bookData.story.storyIndex &&
                button.bookData.story.storyIndex <= nextStoryIndex) {
                button.enabled = true;
                if (button.bookData.story.storyIndex === nextStoryIndex) {
                    button.isUnlocked = true;
                }
            } else {
                button.enabled = false;
            }
        }

        AudioChannel.BGM.play(Assets.audio.bgmMenu.assetName, "loop");

        this.characters.alignCharacters(this.characterXavier, this.characterYadina, this.characterBrad);

        if (pData && pData.openBookId) {
            this.characters.showCharacters();
            this.openBookOnInit(pData);
        } else {
            this.lockScreen();
            this.controls.changeParent(this.background.mainBookshelf);
            this.background.animate(() => this.onAnimateBackground());
        }
    }

    public destroy(pOptions?: boolean | PIXI.DestroyOptions) {
        super.destroy(pOptions);

        AudioChannel.BGM.stop(Assets.audio.bgmMenu.assetName);
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);

        this.buttonMute.setPosition(pSize);
        this.bookOverlay.width = pSize.x * 2;
        this.bookOverlay.height = pSize.y * 2;
    }

    private openBookOnInit(data: IChooseStoryData) {
        const bookButton = this.storyButtons.filter((it) => it.bookData.bookId === data.openBookId)[0];

        if (data.storyFinished) {
            AnalyticsManager.event("Story Creator", "FinishLevel", bookButton.bookData.author);
            AnalyticsManager.event("Story Creator", "level_up", bookButton.bookData.author);
        } else {
            AnalyticsManager.event("Story Creator", "QuitLevel", bookButton.bookData.author);
            AnalyticsManager.event("Story Creator", "endLevel", bookButton.bookData.author);
        }

        this.bookLayer.addChild(this.bookOverlay);
        this.bookLayer.addChild(this.book);

        this.book.visible = true;
        this.bookOverlay.visible = true;
        this.book.buttonHome.visible = false;
        this.book.buttonSpeaker.visible = false;
        this.book.buttonNext.visible = false;

        PubSub.publishSync(HLF.PUSH_KEYBOARD_LAYER, undefined);
        Focusables.add(this.buttonMute);

        const closeBook = () => {
            this.closeBook(bookButton);
            if (data.sceneRender) {
                data.sceneRender.destroy(true);
                data.sceneRender = undefined;
            }
        };

        this.book.openBook(BooksData.filter((it) => it.bookId === data.openBookId)[0], data);
        this.fadeBook(data.storyFinished ? "fadeInLeftPage" : "fadeInRightPage", () => {
            const vo = GameManager.instance.story.outroVO;
            if (data.storyFinished && vo) {
                // if (Application.instance.audio.masterVolume === 0) {
                //     closeBook();
                // } else {
                VoiceOverManager.instance.playVO(vo, closeBook);
                // }
            } else {
                closeBook();
            }
        });
    }

    private onAnimateBackground() {

        this.controls.changeParent(this.main);
        this.controls.zIndex = this.bookLayer.zIndex;
        this.unlockScreen();

        this.characters.showCharacters(() => {

            // if (!Application.instance.saveManager.readBool("welcome-played")) {
            VoiceOverManager.instance.playVO(Assets.audio.libraryIntroVO.map((it) => it.assetName), () => {
                this.startIdleInterval();
            });
            // Application.instance.saveManager.storeBool("welcome-played", true);
            // }

            for (const button of this.storyButtons) {
                if (button.isUnlocked) {
                    button.pulse();
                    button.flashEffect(this.main, () => {
                        button.burstEffect();
                    });
                }
            }

            if (this.buttonFreePlayGroup.isUnlocked) {
                this.buttonFreePlayGroup.visible = true;
                this.buttonFreePlayGroup.changeParent(this.background.backgroundHiRes);
                this.buttonFreePlayGroup.zIndex = this.background.rightBookshelf.zIndex;
                this.buttonFreePlayGroup.moveXFrom(this._size.x, 0.8, Ease.backOut, () => {
                    this.buttonFreePlayGroup.changeParent(this.controls);
                });
            }
        });
    }

    private onClickBook(button: BookButton, bookData: IBookData) {
        AnalyticsManager.event("Story Creator", "select_content", bookData.author);
        AnalyticsManager.event("Story Creator", "SelectAuthorBook", bookData.author);

        this.bookLayer.addChild(this.bookOverlay);
        this.bookLayer.addChild(this.book);
        this.book.buttonHome.onClick.remove();
        this.book.buttonNext.onClick.remove();
        this.book.buttonSpeaker.onClick.remove();

        this.lockScreen();
        this.stopIdleInterval();

        button.stopPulse();
        button.visible = false;
        this.bookOverlay.visible = false;
        this.book.visible = false;

        button.flashEffect(this.main, () => {
            this.book.visible = true;
            this.bookOverlay.visible = true;
            this.book.buttonNext.visible = false;
            this.book.buttonHome.visible = false;
            this.book.buttonSpeaker.visible = true;

            this.bookOverlay.alpha = 0;
            this.bookOverlay.alphaTo(1, 0.4);

            this.book.openBook(bookData, 0.2, () => {
                this.book.buttonHome.onClick.add(() => {
                    VoiceOverManager.instance.stopVO();
                    AnalyticsManager.event("Story Creator", "CloseAuthorBook", bookData.author);
                    this.closeBook(button);
                });
                this.book.buttonNext.onClick.add(() => {
                    AnalyticsManager.event("Story Creator", "StartLevel", bookData.author);
                    AnalyticsManager.event("Story Creator", "startLevel", bookData.author);
                    this.openStory(bookData);
                });
            });
            const bookX = this.book.x;
            const bookY = this.book.y;
            const bookS = this.book.scaleXY;
            this.book.x = button.x;
            this.book.y = button.y;
            this.book.scaleXY = this.bookSmall.scaleXY;
            this.book.tweenTo(0.8, Ease.quadOut, {
                scaleXY: bookS,
                delay: 0.2,
                bezier: {
                    type: "soft",
                    values: [
                        {x: button.x * 0.5, y: this._size.y * 0.5},
                        {x: bookX, y: bookY},
                    ],
                },
                onComplete: () => {
                    this.unlockScreen();
                    PubSub.publishSync(HLF.PUSH_KEYBOARD_LAYER, undefined);
                    const showButtonNext = () => {
                        if (!this.book.buttonNext.visible) {
                            this.book.buttonNext.visible = true;
                            this.book.buttonNext.scaleFrom(0, 0.4, Ease.backOut);
                        }
                        this.startIdleInterval();
                    };
                    const showButtonHome = () => {
                        if (!this.book.buttonHome.visible) {
                            this.book.buttonHome.visible = true;
                            this.book.buttonHome.moveYFrom(
                                this.book.buttonHome.y - this.book.buttonHome.height * 2, 0.4, Ease.backOut);
                        }
                    };
                    if (bookData.storyVO) {
                        if (Application.instance.audio.masterVolume === 0) {
                            showButtonNext();
                        }
                        if (bookData.story?.introVO) {
                            VoiceOverManager.instance.playVO(bookData.story?.introVO, showButtonNext);
                        }
                        this.book.buttonSpeaker.onClick.add(() => {
                            VoiceOverManager.instance.playVO(bookData.storyVO!, {mode: PlayMode.New, skipCC: true});
                        });
                    } else {
                        showButtonNext();
                    }
                    showButtonHome();
                    Focusables.add(
                        this.book.buttonHome,
                        this.book.buttonSpeaker,
                        this.book.buttonNext,
                        this.buttonMute);
                },
            });
            AudioChannel.SFX.play(Assets.audio.bookSlide.assetName);
            this.book.delayedCall(0.2, () => {
                AudioChannel.SFX.play(Assets.audio.bookOpen.assetName);
            });
        });
    }

    private closeBook(button: BookButton) {
        this.lockScreen();
        button.visible = false;
        this.book.closeBook();
        this.bookOverlay.alphaTo(0, 0.2, () => {
            this.bookOverlay.removeFromParent();
        });
        const x = this.book.x;
        const y = this.book.y;
        const s = this.book.scaleXY;
        this.book.tweenTo(0.4, Ease.sineInOut, {
            x: button.x,
            y: button.y,
            scaleXY: this.bookSmall.scaleXY,
            onComplete: () => {
                button.visuals.x = 0;
                button.visuals.y = 0;
                button.visuals.scaleXY = 1;
                button.flashEffect(this.main, () => {
                    this.book.x = x;
                    this.book.y = y;
                    this.book.scaleXY = s;
                    this.book.removeFromParent();
                    button.visible = true;
                    if (button.wasPulsed) {
                        button.pulse();
                    }
                }, () => {
                    this.unlockScreen();
                    PubSub.publishSync(HLF.POP_KEYBOARD_LAYER, undefined);
                    PubSub.publishSync(HLF.FORCE_FOCUS, button);
                    this.startIdleInterval();
                });
            },
        });
    }

    private fadeBook(type: "fadeOut" | "fadeInLeftPage" | "fadeInRightPage", cb?: Callback) {
        const fadeIn = type !== "fadeOut";
        const bookLarge = type === "fadeInLeftPage" ? this.bookLargeLeft : this.bookLargeRight;
        if (!fadeIn) {
            this.book.buttonHome.moveYTo(
                this.book.buttonHome.y - this.book.buttonHome.height * 2, 0.2, Ease.sineIn, fadeIn ? undefined : () => {
                    this.book.buttonHome.visible = false;
                });
        }
        this.book[fadeIn ? "tweenFrom" : "tweenTo"](0.8, fadeIn ? Ease.sineIn : Ease.sineOut, {
            x: bookLarge.x,
            y: bookLarge.y,
            scaleX: bookLarge.scale.x,
            scaleY: bookLarge.scale.y,
            delay: fadeIn ? 0.4 : undefined,
            onComplete: fadeIn ? cb : undefined,
        });
        const overlay = this.bookLayer.addChild(
            new Rect(this._size.x * 2, this._size.y * 2, 0xffffff, 1, Anchor.middleCenter));
        overlay[fadeIn ? "alphaTo" : "alphaFrom"](0, fadeIn ? 0.8 : 0.4, {delay: fadeIn ? 0 : 0.4, onComplete: fadeIn ? undefined : cb});
    }

    private openStory(bookData: IBookData) {
        this.lockScreen();
        this.stopIdleInterval();
        const cb = Times(2, () => {
            if (bookData.story) {
                GameManager.instance.story = bookData.story;
            }
            HLF.AssetMap.addAssetGroup(StoryCreator.NAME, [
                ...Assets.common,
                Assets.paperBackground,
                Assets.audio.bgmStory,
                Assets.audio.buttonPress,
                ...(StoryAssets[bookData.bookId] ? StoryAssets[bookData.bookId]! : []),
                ...StoryAudioAssets(bookData.story!),
                ...StoryInjectionAssets(bookData.story!),
                ...Assets.audio.storyIdle,
            ]);
            this.gotoNextState(StoryCreator.NAME);
        });
        this.fadeBook("fadeOut", cb);
        this.characters.hideCharacters(cb);
    }

    private startIdleInterval() {
        this.stopIdleInterval();
        this._idleInterval = window.setInterval(() => {
            const vo = HLF.ArrayUtils.random(Assets.audio.menuIdle.map((it) => it.assetName).filter((it) => it !== this._lastIdleVO));
            this._lastIdleVO = vo;
            VoiceOverManager.instance.playVO(vo, PlayMode.New);
        }, 14000);
    }

    private stopIdleInterval() {
        if (this._idleInterval !== undefined) {
            clearInterval(this._idleInterval);
            this._idleInterval = undefined;
        }
    }
}

class ButtonFreePlayGroup extends Container {

    public readonly buttonFreePlayPillow = this.addChild(new Sprite());
    public readonly buttonFreePlay = this.addChild(new ShelfButton(() => {
        this._cb();
    }));
    public readonly buttonFreePlayPillowOverlay = this.addChild(new Sprite());

    public isUnlocked = false;

    private readonly _cb: Callback;

    constructor(cb: Callback) {
        super();
        this._cb = cb;
    }
}

class Background extends Container {

    public readonly backgroundLowRes = this.addChild(new Sprite());
    public readonly backgroundHiRes = this.addChild(new Container());
    public readonly backgroundBlurred = this.backgroundHiRes.addChild(new Sprite());
    public readonly yellowLight = this.backgroundHiRes.addChild(new Sprite());
    public readonly mainBookshelf = this.backgroundHiRes.addChild(new (class extends Container {
        public readonly stool = this.addChild(new Sprite());
        public readonly castleBookshelf = this.addChild(new Sprite());
    })());
    public readonly leftBookshelf = this.backgroundHiRes.addChild(new Sprite());
    public readonly rightBookshelf = this.backgroundHiRes.addChild(new Sprite());

    public animate(cb: Callback) {
        // this.loadTextures();

        this.yellowLight.alpha = 0.001;
        const onComplete = Times(2, cb);
        const s = 1536 / this.backgroundLowRes.height;
        this.tweenFrom<Background>(1.6, Ease.sineOut, {
            scaleXY: s,
            y: this.height * s * (this.backgroundLowRes.anchor.y - 0.5),
            delay: 0.8,
            onComplete,
        });
        this.backgroundBlurred.alpha = 0.001;
        this.backgroundBlurred.alphaTo(1, 0.4, {
            delay: 1.2,
            onComplete,
        });
        this.yellowLight.alphaTo(1, 0.4, {delay: 2.6});
    }

    private loadTextures() {
        const sprites = this.getDescendants<Sprite>((it) => it instanceof Sprite).map((it) => new Sprite(it.texture));
        sprites.forEach((it) => {
            it.width = 10;
            it.height = 10;
            this.addChildAt(it, 0);
        });
        this.delayedCall(0.1, () => {
            sprites.forEach((it) => it.destroy());
        });
    }
}

class BookLarge extends Container {
    public readonly book = {};
}
